import React, { useState, useEffect } from 'react';
import AppLoading from '../loading/appLoading';
import { ErrorContainer, MarkdownContainer } from './styles';
import RenderHtml from '../renderHtml';

interface MarkdownPreviewProps {
  fileUrl: string;
  className?: string;
  isTextFile?: boolean;
}

const MarkdownPreview: React.FC<MarkdownPreviewProps> = ({ fileUrl, className, isTextFile = false }) => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchMarkdownContent = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch markdown content: ${response.status}`);
        }

        const textContent = await response.text();
        if (isTextFile) {
          // For text files, preserve line breaks without markdown processing
          setContent(
            `<pre style="white-space: pre-wrap; word-wrap: break-word; font-family: 'Consolas', 'Monaco', 'Courier New', monospace; background-color: var(--background-secondary); padding: var(--space-16); border-radius: var(--radius-md); border: 1px solid var(--border-primary); line-height: 1.5; margin: var(--space-24) 0;">${textContent
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')}</pre>`
          );
        } else {
          const htmlContent = convertMarkdownToHtml(textContent);
          setContent(htmlContent);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load markdown content');
      } finally {
        setLoading(false);
      }
    };

    if (fileUrl) {
      fetchMarkdownContent();
    }
  }, [fileUrl, isTextFile]);

  const convertMarkdownToHtml = (markdown: string): string => {
    const escapeHtml = (text: string): string =>
      text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const applyInlineFormatting = (text: string): string => {
      // Escape HTML first to avoid accidental tag injection
      let t = escapeHtml(text);

      // Images ![alt](src)
      t = t.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" />');
      // Links [text](href)
      t = t.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

      // Inline code `code`
      t = t.replace(/`([^`]+)`/g, '<code>$1</code>');

      // Bold **text**
      t = t.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      // Italic *text*
      t = t.replace(/(^|\W)\*(?!\s)([^*]+?)\*(?=\W|$)/g, '$1<em>$2</em>');

      return t;
    };

    const lines = markdown.replace(/\r\n?/g, '\n').split('\n');

    const htmlParts: string[] = [];
    let inCodeBlock = false;
    let codeBuffer: string[] = [];
    let listType: 'ul' | 'ol' | null = null;
    let blockquoteOpen = false;
    let paragraphOpen = false;
    let inTable = false;

    const closeParagraph = () => {
      if (paragraphOpen) {
        htmlParts.push('</p>');
        paragraphOpen = false;
      }
    };

    const openParagraph = () => {
      if (!paragraphOpen) {
        htmlParts.push('<p>');
        paragraphOpen = true;
      }
    };

    const openList = (type: 'ul' | 'ol') => {
      if (listType !== type) {
        closeParagraph();
        if (listType) {
          htmlParts.push(listType === 'ul' ? '</ul>' : '</ol>');
        }
        htmlParts.push(type === 'ul' ? '<ul>' : '<ol>');
        listType = type;
      }
    };

    const closeList = () => {
      if (listType) {
        htmlParts.push(listType === 'ul' ? '</ul>' : '</ol>');
        listType = null;
      }
    };

    const openBlockquote = () => {
      if (!blockquoteOpen) {
        closeParagraph();
        htmlParts.push('<blockquote>');
        blockquoteOpen = true;
      }
    };

    const closeBlockquote = () => {
      if (blockquoteOpen) {
        closeParagraph();
        htmlParts.push('</blockquote>');
        blockquoteOpen = false;
      }
    };

    const closeTable = () => {
      if (inTable) {
        htmlParts.push('</tbody></table>');
        inTable = false;
      }
    };

    const parseTableRow = (line: string): string[] =>
      line
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((cell) => cell.trim());

    const isTableSeparator = (line: string): boolean => /^\|?(\s*:?-+:?\s*\|)+\s*:?-+:?\s*\|?\s*$/.test(line);

    const getColumnAlignments = (line: string): ('left' | 'center' | 'right' | null)[] =>
      parseTableRow(line).map((cell) => {
        const trimmed = cell.trim();
        if (trimmed.startsWith(':') && trimmed.endsWith(':')) return 'center';
        if (trimmed.endsWith(':')) return 'right';
        if (trimmed.startsWith(':')) return 'left';
        return null;
      });

    for (let i = 0; i < lines.length; i++) {
      const rawLine = lines[i];

      // Handle fenced code blocks ```
      if (/^```/.test(rawLine)) {
        if (!inCodeBlock) {
          // starting code block
          closeParagraph();
          closeList();
          closeBlockquote();
          closeTable();
          inCodeBlock = true;
          codeBuffer = [];
        } else {
          // ending code block
          const code = escapeHtml(codeBuffer.join('\n'));
          htmlParts.push('<pre><code>' + code + '</code></pre>');
          inCodeBlock = false;
          codeBuffer = [];
        }
        continue;
      }

      if (inCodeBlock) {
        codeBuffer.push(rawLine);
        continue;
      }

      const line = rawLine.trimEnd();

      // Horizontal rules
      if (/^(\*\s*\*\s*\*|-{3,}|_{3,})\s*$/.test(line)) {
        closeParagraph();
        closeList();
        closeBlockquote();
        closeTable();
        htmlParts.push('<hr>');
        continue;
      }

      // Headings # .. ######
      const headingMatch = /^(#{1,6})\s+(.+)$/.exec(line);
      if (headingMatch) {
        closeParagraph();
        closeList();
        closeBlockquote();
        closeTable();
        const level = headingMatch[1].length;
        const text = applyInlineFormatting(headingMatch[2].trim());
        htmlParts.push(`<h${level}>${text}</h${level}>`);
        continue;
      }

      // Blockquote
      if (/^>\s?/.test(line)) {
        openBlockquote();
        const content = line.replace(/^>\s?/, '');
        if (content.trim().length === 0) {
          closeParagraph();
        } else {
          openParagraph();
          htmlParts.push(applyInlineFormatting(content));
        }
        // Continue to next line without closing blockquote so consecutive lines stay inside
        continue;
      } else {
        // If we were in a blockquote and current line is not, close it
        if (blockquoteOpen) {
          closeBlockquote();
        }
      }

      // Tables
      const isTableRow = /^\|(.+\|)+\s*$/.test(line) || /^\|(.+\|?)+$/.test(line.trim());
      if (isTableRow && !inTable) {
        const nextLine = i + 1 < lines.length ? lines[i + 1].trimEnd() : '';
        if (isTableSeparator(nextLine)) {
          closeParagraph();
          closeList();
          closeBlockquote();
          const headers = parseTableRow(line);
          const alignments = getColumnAlignments(nextLine);
          htmlParts.push('<table><thead><tr>');
          headers.forEach((header, idx) => {
            const align = alignments[idx];
            const style = align ? ` style="text-align: ${align}"` : '';
            htmlParts.push(`<th${style}>${applyInlineFormatting(header)}</th>`);
          });
          htmlParts.push('</tr></thead><tbody>');
          inTable = true;
          // eslint-disable-next-line no-plusplus
          i++; // skip separator line
          continue;
        }
      }
      if (inTable && isTableRow) {
        const cells = parseTableRow(line);
        htmlParts.push('<tr>');
        cells.forEach((cell) => {
          htmlParts.push(`<td>${applyInlineFormatting(cell)}</td>`);
        });
        htmlParts.push('</tr>');
        continue;
      }
      if (inTable && !isTableRow) {
        closeTable();
      }

      // Lists (no nesting support for now)
      const ulMatch = /^[-*]\s+(.+)$/.exec(line);
      const olMatch = /^\d+\.\s+(.+)$/.exec(line);
      if (ulMatch) {
        openList('ul');
        htmlParts.push('<li>' + applyInlineFormatting(ulMatch[1]) + '</li>');
        continue;
      }
      if (olMatch) {
        openList('ol');
        htmlParts.push('<li>' + applyInlineFormatting(olMatch[1]) + '</li>');
        continue;
      }

      // Blank line closes paragraphs and lists
      if (/^\s*$/.test(line)) {
        closeParagraph();
        closeList();
        closeTable();
        continue;
      }

      // Regular paragraph text
      closeList();
      openParagraph();
      htmlParts.push(applyInlineFormatting(line));
    }

    // Cleanup at EOF
    if (inCodeBlock && codeBuffer.length > 0) {
      const code = escapeHtml(codeBuffer.join('\n'));
      htmlParts.push('<pre><code>' + code + '</code></pre>');
      inCodeBlock = false;
    }
    closeParagraph();
    closeList();
    closeBlockquote();
    closeTable();

    return htmlParts.join('');
  };

  if (loading) {
    return <AppLoading />;
  }

  if (error) {
    return <ErrorContainer>Error: {error}</ErrorContainer>;
  }

  return (
    <MarkdownContainer className='tiptap'>
      <RenderHtml htmlString={content} />
    </MarkdownContainer>
  );
};

export default MarkdownPreview;
